import React, { ReactNode, useMemo, useEffect, useRef } from 'react'
import {
  inspectorHoveredElementContext,
  inspectorSelectedElementContext,
  inspectorContext,
  useInspectorOverlayEvents,
  InspectorContext,
  useHasAnyLayerWithExportableAssets,
} from '../../hooks/inspector'
import { useGetActiveSidebarTab } from '../../../components/ShareSidebarTabContext'
import { useSketchSceneTree } from '../../hooks/inspector/useSketchSceneTree'
import {
  findSelectedSketchElement,
  useCanvasInteractions,
  useCanvasSelection,
} from '@sketch/canvas'
import {
  findSketchElementFromUuid,
  isSceneTreeFullyLoaded,
  ColorFormatProvider,
  InspectorHostProvider,
} from '@sketch/inspector'
import { useComponentsState } from '../../../components/ComponentsStateContext'
import { useCanvasScope } from '../../../../canvas/scope'
import { useVersioning } from '../../../../versioning'
import { SymbolPathLinkDropdown } from '../SymbolPathLinkDropdown'
import { InspectorComponentLink } from '../InspectorComponentLink'
import { InspectCommandBarRegistration } from '../../../commandBarCommands/InspectCommandBarRegistration'
import { useCollapseSelectionOnDismiss } from './useCollapseSelectionOnDismiss'

type FrameInspectorProviderProps = {
  children: ReactNode
}

export function FrameInspectorProvider({
  children,
}: FrameInspectorProviderProps) {
  const scope = useCanvasScope()

  // In a focus view the focused entity is the root of the tree, and is selected
  // on arrival.
  const rootElementUUID = scope?.entityUUID
  const autoSelectRootInitially = !!scope && scope.focus.kind !== 'page'

  const activeSidebarTab = useGetActiveSidebarTab()
  const { selection, selectElement: setSelectedElement } = useCanvasSelection()
  const componentsState = useComponentsState()
  const versioning = useVersioning()
  const isInspectorActive = activeSidebarTab === 'Inspect'

  const { sketchRootElement, hasFailed } = useSketchSceneTree(isInspectorActive)

  // Scoped here rather than inherited from the renderer. Nothing is made the
  // scene root any more - a frame and a Section alike are picked out by fading
  // the rest of the page, which leaves the scene rooted on the page - so a tree
  // that simply took the scene root would show the whole page in a focused
  // entity's inspector.
  //
  // Falls back to the whole tree while the entity is not in it yet: the tree is
  // rebuilt on every fragment load, and a frame deep inside one appears only
  // once that fragment has arrived.
  const inspectorRootElement = useMemo(() => {
    if (!rootElementUUID) return sketchRootElement

    return (
      findSketchElementFromUuid(sketchRootElement, rootElementUUID) ??
      sketchRootElement
    )
  }, [sketchRootElement, rootElementUUID])

  const isTreeFullyLoaded = useMemo(
    () => isSceneTreeFullyLoaded(inspectorRootElement),
    [inspectorRootElement]
  )

  // The selection stores identity only, so this is where it becomes an element,
  // against the tree currently being shown. Re-resolved whenever that tree is
  // rebuilt - which happens on every fragment load - so no consumer can be left
  // holding an element from a tree that no longer exists.
  const selectedElement = useMemo(
    () => findSelectedSketchElement(inspectorRootElement, selection),
    [inspectorRootElement, selection]
  )

  const hasAnyLayerWithExportableAssets =
    useHasAnyLayerWithExportableAssets(inspectorRootElement)

  const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

  // Enable inspector interactions only when inspector is active and canvas interactions are enabled
  const enableInspectorInteractions =
    isInspectorActive && isCanvasInteractionsEnabled

  const {
    hoveredElement,
    setHoveredElement,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  } = useInspectorOverlayEvents(
    inspectorRootElement,
    enableInspectorInteractions,
    autoSelectRootInitially,
    isTreeFullyLoaded
  )

  // The selection outlives a change of scope - another page, or another
  // focused entity - because the provider is not remounted for either. The
  // previously selected element then belongs to something no longer on the
  // canvas, so its guides would linger on the new one. Clear it whenever the
  // scope changes, so a focus view auto-selects its new root instead. The ref
  // skips the initial mount, where clearing would stomp a `?layerPath=`
  // deep-link restore.
  // Null until the page is known: FrameQueries resolves it from the frame
  // query, so it arrives a render or two after mount, and treating that as a
  // scope change would clear a ?layerPath= restore that had already landed.
  const scopeIdentity = scope?.pageUUID
    ? `${scope.pageUUID}:${rootElementUUID ?? ''}`
    : null
  const previousScopeIdentity = useRef<string | null>(null)
  useEffect(() => {
    if (!scopeIdentity) return

    // Adopt the first scope we see rather than resetting into it.
    if (previousScopeIdentity.current === null) {
      previousScopeIdentity.current = scopeIdentity
      return
    }

    if (previousScopeIdentity.current === scopeIdentity) return
    previousScopeIdentity.current = scopeIdentity
    setSelectedElement(null)
  }, [scopeIdentity, setSelectedElement])

  // The inspector package looks components up against the document being
  // viewed; which document and which version is the application's business.
  const inspectorHostDocument = useMemo(
    () => ({
      shareIdentifier: versioning.share.identifier,
      currentVersion: versioning.loading
        ? undefined
        : versioning.currentVersion,
    }),
    [versioning]
  )

  let inspectorStatus: InspectorContext['inspectorStatus'] = 'pending'

  if (inspectorRootElement) {
    inspectorStatus = 'ready' as const
  } else if (hasFailed) {
    inspectorStatus = 'errored' as const
  }

  // Takes the resolved element: this runs above the context that publishes it.
  useCollapseSelectionOnDismiss(isInspectorActive, selectedElement)

  const inspectorContextValue = useMemo((): InspectorContext => {
    return {
      isInspectorActive: isInspectorActive,
      sketchSceneRootElement: inspectorRootElement,
      setHoveredElement,
      inspectorStatus,
      hasAnyLayerWithExportableAssets,
      clickHandler,
      mouseMoveHandler,
      mouseDownHandle,
      isTreeFullyLoaded,
    }
  }, [
    isInspectorActive,
    inspectorRootElement,
    setHoveredElement,
    inspectorStatus,
    hasAnyLayerWithExportableAssets,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
    isTreeFullyLoaded,
  ])

  return (
    <inspectorContext.Provider value={inspectorContextValue}>
      <inspectorHoveredElementContext.Provider value={hoveredElement}>
        <inspectorSelectedElementContext.Provider value={selectedElement}>
          <ColorFormatProvider>
            <InspectorHostProvider
              isIngestingComponents={componentsState === 'PROCESSING'}
              symbolPathDropdown={SymbolPathLinkDropdown}
              componentLink={InspectorComponentLink}
              document={inspectorHostDocument}
            >
              {isInspectorActive && selectedElement && (
                <InspectCommandBarRegistration />
              )}
              {children}
            </InspectorHostProvider>
          </ColorFormatProvider>
        </inspectorSelectedElementContext.Provider>
      </inspectorHoveredElementContext.Provider>
    </inspectorContext.Provider>
  )
}
